import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { submissionSettingsService } from '../../../services/submissionSettingsService';
import { SaveToast, useSaveToast } from './SaveToast';
import { usePlanLimits } from '../../../hooks/usePlanLimits';

// ─── Simple Rich-Text Toolbar ────────────────────────────────────────────────
const TOOLBAR_ACTIONS = [
  { cmd: 'bold',          icon: 'Bold',          title: 'Bold' },
  { cmd: 'italic',        icon: 'Italic',        title: 'Italic' },
  { cmd: 'underline',     icon: 'Underline',     title: 'Underline' },
  { cmd: 'insertUnorderedList', icon: 'List',    title: 'Bullet List' },
  { cmd: 'justifyLeft',   icon: 'AlignLeft',     title: 'Align Left' },
  { cmd: 'justifyCenter', icon: 'AlignCenter',   title: 'Align Center' },
];

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef?.current && editorRef?.current?.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  const execCmd = (cmd) => {
    document.execCommand(cmd, false, null);
    editorRef?.current?.focus();
    onChange(editorRef?.current?.innerHTML || '');
  };

  return (
    <div className="border border-border rounded-md overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30">
        {TOOLBAR_ACTIONS?.map(({ cmd, icon, title }) => (
          <button
            key={cmd}
            type="button"
            title={title}
            onMouseDown={e => { e?.preventDefault(); execCmd(cmd); }}
            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name={icon} size={13} />
          </button>
        ))}
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(editorRef?.current?.innerHTML || '')}
        data-placeholder={placeholder}
        className="min-h-[90px] px-3 py-2 text-sm text-foreground focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
      />
    </div>
  );
};

// ─── Icon Picker ─────────────────────────────────────────────────────────────
const ICON_OPTIONS = [
  { name: 'CheckCircle',   label: 'Check Circle' },
  { name: 'CheckCircle2',  label: 'Check Circle 2' },
  { name: 'ThumbsUp',      label: 'Thumbs Up' },
  { name: 'Star',          label: 'Star' },
  { name: 'Heart',         label: 'Heart' },
  { name: 'Smile',         label: 'Smile' },
  { name: 'PartyPopper',   label: 'Party' },
  { name: 'Rocket',        label: 'Rocket' },
  { name: 'Award',         label: 'Award' },
  { name: 'Gift',          label: 'Gift' },
  { name: 'Mail',          label: 'Mail' },
  { name: 'Send',          label: 'Send' },
];

const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 border border-border rounded-md bg-white hover:bg-muted/30 transition-colors text-sm"
      >
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name={value || 'CheckCircle'} size={16} className="text-primary" />
        </div>
        <span className="text-foreground">{ICON_OPTIONS?.find(i => i?.name === value)?.label || 'Check Circle'}</span>
        <Icon name="ChevronDown" size={13} className="text-muted-foreground ml-1" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-border rounded-lg shadow-lg p-2 grid grid-cols-6 gap-1 w-56">
          {ICON_OPTIONS?.map(opt => (
            <button
              key={opt?.name}
              type="button"
              title={opt?.label}
              onClick={() => { onChange(opt?.name); setOpen(false); }}
              className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors ${value === opt?.name ? 'bg-primary/15 text-primary' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name={opt?.name} size={16} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SubmissionSettingsBar = ({ formId }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('submission');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false); // drives the footer "Changes saved" micro-indicator
  const { toasts, addToast, dismissToast } = useSaveToast(2500);
  const { getFeatureAccess } = usePlanLimits();
  const [newParamKey, setNewParamKey] = useState('');
  const [newParamValue, setNewParamValue] = useState('');
  const [newRecipient, setNewRecipient] = useState('');

  // Ref for the new-param input row — used to detect focus leaving the row
  const paramRowRef = useRef(null);
  // Tracks the latest pending-param inputs so the unmount cleanup can flush them
  const pendingParamRef = useRef({ key: '', value: '' });
  // True while loadSettings is running — suppress auto-save during initial population
  const isLoadingRef = useRef(false);
  // True once the first load has completed
  const hasLoadedRef = useRef(false);
  // Debounce timer ref for auto-save
  const debounceTimerRef = useRef(null);
  // Ref to hold latest settings for the debounced callback
  const settingsRef = useRef(null);

  const [settings, setSettings] = useState({
    // Thank You Screen
    thankYouEnabled: false,
    thankYouIcon: 'CheckCircle',
    thankYouHeader: 'Thank you for your submission!',
    thankYouMessage: '<p>We have received your response and will be in touch shortly.</p>',
    thankYouShowSummary: false,
    thankYouShowTicket: false,

    // URL Redirect
    redirectEnabled: false,
    redirectUrl: '',
    redirectPassParams: false,
    redirectParams: [], // [{ key, value }]

    // Spam protection
    turnstileEnabled: false,

    // Submission notifications
    emailNotificationsEnabled: false,
    emailNotificationRecipients: [],
  });
  const turnstileAccess = getFeatureAccess('starter', 'turnstile');

  useEffect(() => {
    if (formId) loadSettings();
  }, [formId]);

  const loadSettings = async () => {
    isLoadingRef.current = true;
    try {
      const data = await submissionSettingsService.getSettings(formId);
      if (data) setSettings(prev => ({ ...prev, ...data }));
    } catch (err) {
      addToast(err?.message || 'Failed to load submission settings. Please refresh and try again.', 'error');
    }
    // Mark as done in a microtask so the state update from setSettings
    // is flushed before we allow auto-save to arm itself
    setTimeout(() => {
      isLoadingRef.current = false;
      hasLoadedRef.current = true;
    }, 0);
  };

  // Keep settingsRef in sync so the debounced callback always sees the latest value
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Keep pendingParamRef in sync so the unmount cleanup sees the latest input values
  useEffect(() => {
    pendingParamRef.current = { key: newParamKey, value: newParamValue };
  }, [newParamKey, newParamValue]);

  // On unmount: flush any pending param entry straight to the backend so it
  // survives page navigation (the debounce timer would be cancelled on unmount)
  useEffect(() => {
    return () => {
      const { key, value } = pendingParamRef.current;
      const currentSettings = settingsRef.current;
      if (key?.trim() && formId && currentSettings) {
        const nextParams = [
          ...(currentSettings?.redirectParams || []),
          { key: key.trim(), value: value?.trim() || '' },
        ];
        const nextSettings = { ...currentSettings, redirectParams: nextParams };
        // Fire-and-forget — component is unmounting, we cannot await
        submissionSettingsService.saveSettings(formId, nextSettings).catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Immediately persist settings to the backend, bypassing the debounce.
  // Used when we auto-commit a pending param on blur/collapse so the save
  // cannot be cancelled by a subsequent unmount.
  // Note: URL validation is intentionally skipped here — an invalid redirect
  // URL must never prevent a newly typed param from being persisted; the
  // debounced auto-save will surface that validation error separately.
  const saveNow = async (overrideSettings) => {
    const current = overrideSettings ?? settingsRef.current;
    if (!formId || !current) return;
    // Cancel any in-flight debounce so we don't double-save
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    try {
      setSaving(true);
      await submissionSettingsService.saveSettings(formId, current);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      addToast(err?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on any settings change (debounced), skipping the initial mount/load
  useEffect(() => {
    if (!hasLoadedRef.current || isLoadingRef.current || !formId) return;

    // Clear any pending debounce
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(async () => {
      const current = settingsRef.current;
      // Validate redirect URL only if redirect is enabled and URL is non-empty
      if (current?.redirectEnabled && current?.redirectUrl) {
        try { new URL(current.redirectUrl); } catch (_) {
          addToast('Please enter a valid redirect URL (include https://).', 'error');
          return;
        }
      }
      try {
        setSaving(true);
        await submissionSettingsService.saveSettings(formId, current);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        addToast('Submission settings saved.', 'success');
      } catch (err) {
        addToast(err?.message || 'Failed to save settings', 'error');
      } finally {
        setSaving(false);
      }
    }, 800);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [settings, formId]);

  const handleSave = async () => {
    if (!formId) {
      addToast('Save the form first before configuring submission settings.', 'error');
      return;
    }
    // Validate redirect URL if enabled
    if (settings?.redirectEnabled && settings?.redirectUrl) {
      try { new URL(settings.redirectUrl); } catch (_) {
        addToast('Please enter a valid redirect URL (include https://).', 'error');
        return;
      }
    }
    try {
      setSaving(true);
      await submissionSettingsService.saveSettings(formId, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      addToast('Submission settings saved.', 'success');
    } catch (err) {
      addToast(err?.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddParam = () => {
    const key = newParamKey?.trim();
    const val = newParamValue?.trim();
    if (!key) return;
    setSettings(prev => ({
      ...prev,
      redirectParams: [...(prev?.redirectParams || []), { key, value: val }],
    }));
    setNewParamKey('');
    setNewParamValue('');
  };

  // Commit the pending param row when focus leaves it and immediately persist,
  // so the entry survives even if the user navigates away right after.
  const handleParamRowBlur = (e) => {
    if (!paramRowRef.current?.contains(e.relatedTarget) && newParamKey?.trim()) {
      const key = newParamKey.trim();
      const val = newParamValue.trim();
      const nextParams = [...(settingsRef.current?.redirectParams || []), { key, value: val }];
      const nextSettings = { ...settingsRef.current, redirectParams: nextParams };
      setSettings(nextSettings);
      setNewParamKey('');
      setNewParamValue('');
      saveNow(nextSettings);
    }
  };

  const handleRemoveParam = (idx) => {
    setSettings(prev => ({
      ...prev,
      redirectParams: prev?.redirectParams?.filter((_, i) => i !== idx),
    }));
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      addToast('Enter a valid recipient email address.', 'error');
      return;
    }
    if (settings?.emailNotificationRecipients?.includes(email)) {
      setNewRecipient('');
      return;
    }
    if ((settings?.emailNotificationRecipients?.length || 0) >= 20) {
      addToast('You can add up to 20 notification recipients.', 'error');
      return;
    }
    setSettings(prev => ({
      ...prev,
      emailNotificationRecipients: [...(prev?.emailNotificationRecipients || []), email],
    }));
    setNewRecipient('');
  };

  const handleRemoveRecipient = (email) => {
    setSettings(prev => {
      const recipients = (prev?.emailNotificationRecipients || []).filter(item => item !== email);
      return {
        ...prev,
        emailNotificationRecipients: recipients,
        emailNotificationsEnabled: recipients.length > 0 ? prev.emailNotificationsEnabled : false,
      };
    });
  };

  // Mutual exclusivity: only one of thankYou / redirect can be active
  const handleToggleThankYou = (checked) => {
    setSettings(prev => ({
      ...prev,
      thankYouEnabled: checked,
      redirectEnabled: checked ? false : prev?.redirectEnabled,
    }));
  };

  const handleToggleRedirect = (checked) => {
    setSettings(prev => ({
      ...prev,
      redirectEnabled: checked,
      thankYouEnabled: checked ? false : prev?.thankYouEnabled,
    }));
  };

  // ── Status badge in collapsed header ──────────────────────────────────────
  const postSubmitBadge = settings?.thankYouEnabled
    ? { icon: 'CheckCircle', label: 'Custom Thank You', color: 'text-green-700 bg-green-50 border-green-200' }
    : settings?.redirectEnabled
    ? { icon: 'CheckCircle', label: 'URL Redirect', color: 'text-green-700 bg-[#f0feee] border-green-200' }
    : null;
  const notificationRecipientCount = settings?.emailNotificationRecipients?.length || 0;

  return (
    <div className="border-b border-border shadow-sm" style={{ backgroundColor: 'rgba(236, 245, 238, 0.5)' }}>
      {/* ── Collapsed header ── */}
      <button
        onClick={() => {
          // Auto-commit any pending param entry before collapsing and persist
          // immediately so it survives if the user navigates away next
          if (expanded && newParamKey?.trim()) {
            const key = newParamKey.trim();
            const val = newParamValue.trim();
            const nextParams = [...(settingsRef.current?.redirectParams || []), { key, value: val }];
            const nextSettings = { ...settingsRef.current, redirectParams: nextParams };
            setSettings(nextSettings);
            setNewParamKey('');
            setNewParamValue('');
            saveNow(nextSettings);
          }
          setExpanded(v => !v);
        }}
        className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/40 transition-colors group"
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary/10">
            <Icon name="Send" size={15} className="text-primary" />
          </div>
          <span className="text-sm font-semibold text-foreground">Submission Settings</span>
          {postSubmitBadge && (
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${postSubmitBadge?.color}`}>
              <Icon name={postSubmitBadge?.icon} size={11} />
              {postSubmitBadge?.label}
            </span>
          )}
          {notificationRecipientCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-primary/20 bg-primary/5 text-primary"
              aria-label={`${notificationRecipientCount} notification ${notificationRecipientCount === 1 ? 'recipient' : 'recipients'}`}
            >
              <Icon name="Mail" size={11} />
              {notificationRecipientCount} Recipients
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
            Configure what happens after submission
          </span>
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
        </div>
      </button>
      {/* ── Expanded panel ── */}
      {expanded && (
        <div className="px-6 pb-6 pt-2 border-t border-border/50 bg-muted/20">
          <div className="max-w-3xl space-y-6">
            <div
              role="tablist"
              aria-label="Submission configuration"
              className="inline-flex w-full sm:w-auto rounded-lg border border-border bg-white p-1 shadow-sm"
            >
              {[
                { id: 'submission', label: 'Submission Settings', icon: 'Send' },
                { id: 'notifications', label: 'Notifications', icon: 'Mail' },
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeSettingsTab === tab.id}
                  tabIndex={activeSettingsTab === tab.id ? 0 : -1}
                  onClick={() => setActiveSettingsTab(tab.id)}
                  onKeyDown={event => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const nextTab = event.key === 'ArrowLeft' || event.key === 'Home'
                      ? 'submission'
                      : 'notifications';
                    setActiveSettingsTab(nextTab);
                    event.currentTarget.parentElement
                      ?.querySelector(`[data-settings-tab="${nextTab}"]`)
                      ?.focus();
                  }}
                  data-settings-tab={tab.id}
                  className={`flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    activeSettingsTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={14} />
                  {tab.label}
                  {tab.id === 'notifications' && notificationRecipientCount > 0 && (
                    <span
                      className={`inline-flex min-w-5 h-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold leading-none ${
                        activeSettingsTab === tab.id
                          ? 'bg-white/20 text-primary-foreground'
                          : 'bg-primary/10 text-primary'
                      }`}
                      aria-label={`${notificationRecipientCount} ${notificationRecipientCount === 1 ? 'recipient' : 'recipients'}`}
                    >
                      {notificationRecipientCount} Recipients
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Post-Submission Action ── */}
            {activeSettingsTab === 'submission' && (
            <div role="tabpanel" className="space-y-4">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Icon name="LayoutTemplate" size={13} className="text-primary" />
                Post-Submission Action
                <span className="text-xs font-normal text-muted-foreground normal-case tracking-normal ml-1">(choose one)</span>
              </h3>

              {/* ── 2a: Custom Thank You Screen ── */}
              <div className={`rounded-lg border transition-colors ${settings?.thankYouEnabled ? 'border-primary/40 bg-primary/5' : 'border-border bg-white'}`}>
                {/* Toggle header */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                  onClick={() => handleToggleThankYou(!settings?.thankYouEnabled)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${settings?.thankYouEnabled ? 'bg-primary/15' : 'bg-muted'}`}>
                      <Icon name="CheckCircle2" size={16} className={settings?.thankYouEnabled ? 'text-primary' : 'text-muted-foreground'} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Custom Thank You Screen</p>
                      <p className="text-xs text-muted-foreground">Show a branded confirmation page after submission</p>
                    </div>
                  </div>
                  {/* Toggle switch */}
                  <div className={`relative w-10 h-5 rounded-full border transition-colors ${settings?.thankYouEnabled ? 'bg-primary border-primary' : 'bg-slate-300 border-slate-400'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${settings?.thankYouEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </div>

                {/* Expanded config */}
                {settings?.thankYouEnabled && (
                  <div className="px-4 pb-4 space-y-4 border-t border-primary/20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      {/* Icon picker */}
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Confirmation Icon</label>
                        <IconPicker
                          value={settings?.thankYouIcon}
                          onChange={v => setSettings(prev => ({ ...prev, thankYouIcon: v }))}
                        />
                      </div>

                      {/* Header */}
                      <div>
                        <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Header Text</label>
                        <input
                          type="text"
                          value={settings?.thankYouHeader || ''}
                          onChange={e => setSettings(prev => ({ ...prev, thankYouHeader: e?.target?.value }))}
                          placeholder="Thank you for your submission!"
                          className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                        />
                      </div>

                      {/* Rich-text message */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Message Body</label>
                        <RichTextEditor
                          value={settings?.thankYouMessage}
                          onChange={v => setSettings(prev => ({ ...prev, thankYouMessage: v }))}
                          placeholder="Write a custom message for your respondents…"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Supports bold, italic, underline, and bullet lists</p>
                      </div>

                      {/* Display options */}
                      <div className="md:col-span-2 space-y-2">
                        <label className="block text-xs font-semibold text-foreground mb-1 uppercase tracking-wide">Display Options</label>

                        <label className="flex items-start gap-3 bg-white border border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/20 transition-colors">
                          <input
                            type="checkbox"
                            checked={settings?.thankYouShowSummary || false}
                            onChange={e => setSettings(prev => ({ ...prev, thankYouShowSummary: e?.target?.checked }))}
                            className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">Show Submission Summary</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Display a read-only recap of the submitted field values on the confirmation screen</p>
                          </div>
                        </label>

                        <label className="flex items-start gap-3 bg-white border border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/20 transition-colors">
                          <input
                            type="checkbox"
                            checked={settings?.thankYouShowTicket || false}
                            onChange={e => setSettings(prev => ({ ...prev, thankYouShowTicket: e?.target?.checked }))}
                            className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">Show Unique Tracking / Ticket Number</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Auto-generate a reference number (e.g. <code className="bg-muted px-1 rounded">TKT-20240803-0042</code>) displayed to the submitter for follow-up</p>
                          </div>
                        </label>
                      </div>

                      {/* Live preview chip */}
                      <div className="md:col-span-2">
                        <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 px-4 py-4 flex flex-col items-center gap-2 text-center">
                          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                            <Icon name={settings?.thankYouIcon || 'CheckCircle'} size={22} className="text-primary" />
                          </div>
                          <p className="text-sm font-semibold text-foreground">{settings?.thankYouHeader || 'Thank you for your submission!'}</p>
                          {settings?.thankYouMessage && (
                            <div
                              className="text-xs text-muted-foreground max-w-xs leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: settings?.thankYouMessage }}
                            />
                          )}
                          {settings?.thankYouShowTicket && (
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-muted border border-border rounded-full px-3 py-1 text-foreground">
                              <Icon name="Hash" size={11} />TKT-20240803-0042
                            </span>
                          )}
                          {settings?.thankYouShowSummary && (
                            <div className="w-full max-w-xs bg-white border border-border rounded-md px-3 py-2 text-left space-y-1">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Submission Summary</p>
                              <div className="text-xs text-muted-foreground italic">Field values will appear here…</div>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground/60 mt-1">— Preview —</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── 2b: URL Redirect ── */}
              <div className={`rounded-lg border transition-colors ${settings?.redirectEnabled ? 'border-blue-400/50 bg-blue-50/40' : 'border-border bg-white'}`}>
                {/* Toggle header */}
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                  onClick={() => handleToggleRedirect(!settings?.redirectEnabled)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${settings?.redirectEnabled ? 'bg-blue-100' : 'bg-muted'}`}>
                      <Icon name="ExternalLink" size={16} className={settings?.redirectEnabled ? 'text-blue-600' : 'text-muted-foreground'} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">URL Redirect (Forwarding)</p>
                      <p className="text-xs text-muted-foreground">Send submitters to a custom URL after they submit</p>
                    </div>
                  </div>
                  <div className={`relative w-10 h-5 rounded-full border transition-colors ${settings?.redirectEnabled ? 'bg-blue-500 border-blue-600' : 'bg-slate-300 border-slate-400'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${settings?.redirectEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </div>

                {/* Expanded config */}
                {settings?.redirectEnabled && (
                  <div className="px-4 pb-4 space-y-4 border-t border-blue-200/60">
                    {/* Redirect URL */}
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide">Redirect URL</label>
                      <input
                        type="url"
                        value={settings?.redirectUrl || ''}
                        onChange={e => setSettings(prev => ({ ...prev, redirectUrl: e?.target?.value }))}
                        placeholder="https://yourdomain.com/thank-you"
                        className="w-full text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400 transition-colors"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Submitters will be forwarded here immediately after a successful submission (e.g. a landing page, booking calendar, or upsell page)
                      </p>
                    </div>

                    {/* Pass submission data */}
                    <label className="flex items-start gap-3 bg-white border border-border rounded-md px-4 py-3 cursor-pointer hover:bg-muted/20 transition-colors">
                      <input
                        type="checkbox"
                        checked={settings?.redirectPassParams || false}
                        onChange={e => setSettings(prev => ({ ...prev, redirectPassParams: e?.target?.checked }))}
                        className="mt-0.5 w-4 h-4 accent-blue-500 cursor-pointer"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">Pass submission data as URL query parameters</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Append field values to the redirect URL so the destination page can read them (e.g. <code className="bg-muted px-1 rounded">?name=John&email=john@example.com</code>)
                        </p>
                      </div>
                    </label>

                    {/* Custom query params */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
                        <Icon name="Link2" size={12} />
                        Custom Query Parameters
                        <span className="font-normal text-muted-foreground normal-case tracking-normal">(optional)</span>
                      </label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Add static key/value pairs that are always appended to the redirect URL. Use <code className="bg-muted px-1 rounded">{'{{field_name}}'}</code> as the value to inject a submission field dynamically.
                      </p>

                      {/* Existing params */}
                      {settings?.redirectParams?.length > 0 && (
                        <div className="space-y-1.5 mb-2">
                          {settings?.redirectParams?.map((param, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white border border-border rounded-md px-3 py-2">
                              <code className="text-xs font-mono text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{param?.key}</code>
                              <Icon name="Equal" size={12} className="text-muted-foreground flex-shrink-0" />
                              <code className="text-xs font-mono text-foreground flex-1 truncate">{param?.value || '(empty)'}</code>
                              <button
                                onClick={() => handleRemoveParam(idx)}
                                className="text-muted-foreground hover:text-red-500 transition-colors ml-auto"
                              >
                                <Icon name="X" size={13} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add new param */}
                      <div
                        ref={paramRowRef}
                        onBlur={handleParamRowBlur}
                        className="flex gap-2"
                      >
                        <input
                          type="text"
                          value={newParamKey}
                          onChange={e => setNewParamKey(e?.target?.value)}
                          onKeyDown={e => { if (e?.key === 'Enter') { e?.preventDefault(); handleAddParam(); } }}
                          placeholder="key"
                          className="w-28 text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400 transition-colors font-mono"
                        />
                        <span className="flex items-center text-muted-foreground text-sm">=</span>
                        <input
                          type="text"
                          value={newParamValue}
                          onChange={e => setNewParamValue(e?.target?.value)}
                          onKeyDown={e => { if (e?.key === 'Enter') { e?.preventDefault(); handleAddParam(); } }}
                          placeholder="value or {{field_name}}"
                          className="flex-1 text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-400 transition-colors font-mono"
                        />
                        <button
                          onClick={handleAddParam}
                          disabled={!newParamKey?.trim()}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <Icon name="Plus" size={14} />Add
                        </button>
                      </div>

                      {/* URL preview */}
                      {settings?.redirectUrl && (
                        <div className="mt-3 rounded-md bg-muted/50 border border-border px-3 py-2">
                          <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">URL Preview</p>
                          <p className="text-xs font-mono text-foreground break-all">
                            {settings?.redirectUrl}
                            {(settings?.redirectPassParams || settings?.redirectParams?.length > 0) && '?'}
                            {settings?.redirectPassParams && <span className="text-blue-600">{'<submission_fields>'}</span>}
                            {settings?.redirectPassParams && settings?.redirectParams?.length > 0 && '&'}
                            {settings?.redirectParams?.map((p, i) => (
                              <span key={i}>
                                {i > 0 && '&'}
                                <span className="text-blue-600">{p?.key}</span>
                                {'='}
                                <span className="text-green-700">{p?.value || ''}</span>
                              </span>
                            ))}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Mutual exclusivity note */}
              {!settings?.thankYouEnabled && !settings?.redirectEnabled && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Icon name="Info" size={12} />
                  No post-submission action configured — the default browser confirmation will be shown. Enable one option above.
                </p>
              )}
            </div>
            )}

            {/* ── Submission notifications ── */}
            {activeSettingsTab === 'notifications' && (
            <div role="tabpanel" className="space-y-3">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Icon name="Mail" size={13} className="text-primary" />
                Submission Notifications
              </h3>
              <div className={`rounded-lg border transition-colors ${
                settings?.emailNotificationsEnabled ? 'border-primary/40 bg-primary/5' : 'border-border bg-white'
              }`}>
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      settings?.emailNotificationsEnabled ? 'bg-primary/15' : 'bg-muted'
                    }`}>
                      <Icon name="Mail" size={16} className={
                        settings?.emailNotificationsEnabled ? 'text-primary' : 'text-muted-foreground'
                      } />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Email recipients</p>
                      <p className="text-xs text-muted-foreground">Send a copy of each new submission to one or more inboxes</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!settings?.emailNotificationsEnabled && !settings?.emailNotificationRecipients?.length) {
                        addToast('Add at least one recipient before enabling notifications.', 'error');
                        return;
                      }
                      setSettings(prev => ({ ...prev, emailNotificationsEnabled: !prev.emailNotificationsEnabled }));
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 flex-shrink-0 ${
                      settings?.emailNotificationsEnabled ? 'bg-primary border-primary' : 'bg-slate-300 border-slate-400'
                    }`}
                    aria-label="Toggle submission email notifications"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${
                      settings?.emailNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="border-t border-border px-4 py-4 space-y-3">
                  {settings?.emailNotificationRecipients?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {settings.emailNotificationRecipients.map(email => (
                        <span key={email} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground">
                          {email}
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(email)}
                            aria-label={`Remove ${email}`}
                            className="text-muted-foreground hover:text-red-600"
                          >
                            <Icon name="X" size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={e => setNewRecipient(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          handleAddRecipient();
                        }
                      }}
                      placeholder="notifications@example.com"
                      className="flex-1 text-sm border border-border rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={handleAddRecipient}
                      disabled={!newRecipient.trim()}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                    >
                      <Icon name="Plus" size={14} /> Add recipient
                    </button>
                  </div>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Notification emails include submitted answers. Only add recipients authorized to view this form’s responses.
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* ── Spam protection ── */}
            {activeSettingsTab === 'submission' && (
            <div role="tabpanel" className="space-y-3">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Icon name="ShieldCheck" size={13} className="text-primary" />
                Spam Protection
              </h3>
              <div className={`rounded-lg border transition-colors ${
                settings?.turnstileEnabled && turnstileAccess?.allowed
                  ? 'border-primary/40 bg-primary/5'
                  : 'border-border bg-white'
              }`}>
                <div className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      settings?.turnstileEnabled && turnstileAccess?.allowed ? 'bg-primary/15' : 'bg-muted'
                    }`}>
                      <Icon name="ShieldCheck" size={16} className={
                        settings?.turnstileEnabled && turnstileAccess?.allowed ? 'text-primary' : 'text-muted-foreground'
                      } />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Cloudflare Turnstile</p>
                      <p className="text-xs text-muted-foreground">
                        {turnstileAccess?.loading
                          ? 'Checking plan access…'
                          : turnstileAccess?.allowed
                          ? 'Add a privacy-friendly anti-bot check before a form submits'
                          : 'Available on Starter Pro and above'}
                      </p>
                    </div>
                  </div>
                  {turnstileAccess?.allowed && !turnstileAccess?.loading ? (
                    <button
                      type="button"
                      onClick={() => setSettings(prev => ({ ...prev, turnstileEnabled: !prev?.turnstileEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 flex-shrink-0 ${
                        settings?.turnstileEnabled ? 'bg-primary border-primary' : 'bg-slate-300 border-slate-400'
                      }`}
                      aria-label="Toggle Cloudflare Turnstile"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-1 ring-black/10 transition-transform ${
                        settings?.turnstileEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                      <Icon name="Lock" size={11} />
                      Upgrade required
                    </span>
                  )}
                </div>
                {turnstileAccess?.allowed && settings?.turnstileEnabled && (
                  <div className="border-t border-primary/20 px-4 py-3 text-xs text-muted-foreground">
                    Turnstile is enabled for this form. WebForms manages the Cloudflare keys; respondents only see the challenge when they submit.
                  </div>
                )}
              </div>
            </div>
            )}

            {/* ── Save button ── */}
            <div className="flex items-center justify-between pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                {saving ? (
                  <>
                    <Icon name="Loader2" size={12} className="inline animate-spin" />
                    Auto-saving…
                  </>
                ) : saved ? (
                  <>
                    <Icon name="CheckCircle" size={12} className="inline text-green-600" />
                    <span className="text-green-700">Changes saved</span>
                  </>
                ) : (
                  <>
                    <Icon name="Info" size={12} className="inline" />
                    Changes are saved automatically
                  </>
                )}
              </p>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-60 transition-colors"
              >
                <Icon name={saving ? 'Loader2' : 'Save'} size={14} className={saving ? 'animate-spin' : ''} />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

          </div>
        </div>
      )}
      <SaveToast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default SubmissionSettingsBar;
